import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobsAPI, applicationsAPI, formatApiError } from '../services/api';
import Button from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import {
  Building,
  MapPin,
  Briefcase,
  DollarSign,
  GraduationCap,
  Calendar,
  CheckCircle2,
  UploadCloud,
  FileText,
  X,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import './PublicJob.css';

export default function PublicJob() {
  const { jobId } = useParams();
  const { user } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Application State
  const [applied, setApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applySuccess, setApplySuccess] = useState('');
  const [applyError, setApplyError] = useState('');

  // Unauthenticated Candidate Form
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchJob = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await jobsAPI.getPublished(jobId);
      setJob(res.data);
    } catch (err) {
      setError(formatApiError(err, 'Job opening not found or has been closed.'));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // Check if logged in user already applied
  useEffect(() => {
    if (user && user.role === 'CANDIDATE') {
      applicationsAPI
        .getMyApplications()
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data?.applications || [];
          if (list.some((a) => a.job_id === jobId)) {
            setApplied(true);
          }
        })
        .catch(() => {});
    }
  }, [user, jobId]);

  // 1-Click apply for logged-in candidates
  const handleLoggedInApply = async () => {
    try {
      setSubmitting(true);
      setApplyError('');
      await applicationsAPI.apply(jobId);
      setApplied(true);
      setApplySuccess('Application submitted! Your profile and primary CV have been sent to the employer.');
    } catch (err) {
      setApplyError(formatApiError(err, 'Failed to submit application. Make sure your primary CV is uploaded.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Quick Apply for external candidates coming from LinkedIn
  const handlePublicSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setApplyError('Please fill in your name and email.');
      return;
    }
    if (!selectedFile) {
      setApplyError('Please attach your PDF resume.');
      return;
    }

    try {
      setSubmitting(true);
      setApplyError('');
      const data = new FormData();
      data.append('first_name', formData.firstName.trim());
      data.append('last_name', formData.lastName.trim());
      data.append('email', formData.email.trim());
      if (formData.phone) data.append('phone', formData.phone.trim());
      data.append('file', selectedFile);

      const res = await jobsAPI.applyPublic(jobId, data);
      setApplied(true);
      setApplySuccess(res.data.message || 'Application submitted successfully!');
    } catch (err) {
      setApplyError(formatApiError(err, 'Failed to submit your application.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-job-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Sparkles className="animate-spin" size={24} color="#6366f1" />
          <span>Loading Career Opening...</span>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="public-job-page">
        <header className="public-navbar">
          <Link to="/" className="public-logo">
            <span className="public-logo-badge">⚡</span>
            <span>SmartHire Careers</span>
          </Link>
        </header>
        <div className="public-container" style={{ textAlign: 'center', marginTop: '60px' }}>
          <h2 style={{ fontSize: '1.75rem', marginBottom: '12px' }}>Position Not Found</h2>
          <p style={{ color: '#94a3b8', marginBottom: '24px' }}>
            {error || 'This job opening is either no longer accepting applications or does not exist.'}
          </p>
          <Link to="/jobs">
            <Button variant="primary">Browse All Openings</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Competitive Compensation';
    if (min && max) return `$${Number(min).toLocaleString()} – $${Number(max).toLocaleString()} / year`;
    return `$${Number(min || max).toLocaleString()} / year`;
  };

  return (
    <div className="public-job-page">
      {/* Top Navbar */}
      <header className="public-navbar">
        <Link to="/" className="public-logo">
          <span className="public-logo-badge">⚡</span>
          <span>SmartHire Careers</span>
        </Link>
        <div className="public-nav-actions">
          {user ? (
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="primary" size="sm">
                  Join SmartHire
                </Button>
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="public-container">
        {/* Job Hero Card */}
        <div className="job-hero-card">
          <div className="job-hero-header">
            <div>
              <div className="job-company-pill">
                <Building size={14} />
                <span>{job.company_name || 'Hiring Organization'}</span>
              </div>
              <h1 className="job-title-large">{job.title}</h1>
            </div>
            <div className="job-status-badge-public">
              <span className="job-status-dot"></span>
              <span>Actively Hiring</span>
            </div>
          </div>

          <div className="job-meta-row">
            <div className="meta-chip">
              <MapPin size={15} color="#818cf8" />
              <span>
                Location: <strong>{job.location || 'Remote'}</strong>
              </span>
            </div>
            <div className="meta-chip">
              <Briefcase size={15} color="#818cf8" />
              <span>
                Type: <strong>{job.employment_type?.replace('_', ' ') || 'Full Time'}</strong>
              </span>
            </div>
            <div className="meta-chip">
              <DollarSign size={15} color="#10b981" />
              <span>
                Salary: <strong>{formatSalary(job.salary_min, job.salary_max)}</strong>
              </span>
            </div>
            {job.education_level && (
              <div className="meta-chip">
                <GraduationCap size={15} color="#818cf8" />
                <span>
                  Education: <strong>{job.education_level}</strong>
                </span>
              </div>
            )}
            {job.experience_min != null && (
              <div className="meta-chip">
                <Calendar size={15} color="#818cf8" />
                <span>
                  Experience: <strong>{job.experience_min}+ Years</strong>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 2-Column Layout: Details + Apply Form */}
        <div className="job-details-grid">
          {/* Left Column: Job Description */}
          <div className="job-main-content">
            <div className="content-section">
              <h2 className="section-heading">
                <Sparkles size={18} color="#6366f1" />
                About This Role
              </h2>
              <div className="section-body">
                {job.description ||
                  'We are seeking a talented professional to join our fast-growing team. In this role, you will collaborate on mission-critical initiatives and leverage modern technologies.'}
              </div>
            </div>

            <div className="content-section">
              <h2 className="section-heading">
                <CheckCircle2 size={18} color="#10b981" />
                Role Requirements & Qualifications
              </h2>
              <ul style={{ paddingLeft: '20px', color: '#cbd5e1', lineHeight: '1.8' }}>
                <li>Proven track record relevant to {job.title}.</li>
                <li>Strong problem-solving, architectural, and communication abilities.</li>
                <li>Comfortable collaborating in dynamic agile environments.</li>
                {job.experience_min && <li>At least {job.experience_min} years of relevant experience.</li>}
                {job.education_level && <li>Degree or equivalent in {job.education_level}.</li>}
              </ul>
            </div>
          </div>

          {/* Right Column: Instant Application Card */}
          <aside className="apply-sidebar-card">
            <h3 className="apply-sidebar-title">Apply for this Role</h3>
            <p className="apply-sidebar-desc">
              Your resume will be processed by our automated AI recruitment screening engine.
            </p>

            {applySuccess ? (
              <div
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid #10b981',
                  borderRadius: '10px',
                  padding: '16px',
                  color: '#10b981',
                  textAlign: 'center',
                }}
              >
                <CheckCircle2 size={32} style={{ margin: '0 auto 8px' }} />
                <p style={{ fontWeight: 600, fontSize: '0.9375rem', marginBottom: '4px' }}>Application Received!</p>
                <p style={{ fontSize: '0.8125rem', color: '#cbd5e1' }}>{applySuccess}</p>
              </div>
            ) : applied ? (
              <div
                style={{
                  background: 'rgba(99, 102, 241, 0.1)',
                  border: '1px solid #6366f1',
                  borderRadius: '10px',
                  padding: '16px',
                  textAlign: 'center',
                  color: '#818cf8',
                }}
              >
                <CheckCircle2 size={28} style={{ margin: '0 auto 6px' }} />
                <p style={{ fontWeight: 600 }}>Already Applied</p>
                <p style={{ fontSize: '0.8125rem', color: '#cbd5e1', marginTop: '4px' }}>
                  Your application is active and under review by the hiring team.
                </p>
              </div>
            ) : user && user.role === 'CANDIDATE' ? (
              // Logged in candidate 1-click apply
              <div>
                <p style={{ fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '16px' }}>
                  Logged in as <strong>{user.email}</strong>. Apply instantly using your saved primary CV.
                </p>
                {applyError && (
                  <div className="alert alert-error" style={{ marginBottom: '12px' }}>
                    <span>⚠️ {applyError}</span>
                  </div>
                )}
                <Button
                  variant="primary"
                  loading={submitting}
                  onClick={handleLoggedInApply}
                  style={{ width: '100%' }}
                  icon={Sparkles}
                >
                  Apply to Job
                </Button>
              </div>
            ) : (
              // External candidate Quick Apply Form
              <form onSubmit={handlePublicSubmit} className="quick-apply-form">
                {applyError && (
                  <div
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid #ef4444',
                      borderRadius: '8px',
                      padding: '10px',
                      color: '#f87171',
                      fontSize: '0.8125rem',
                    }}
                  >
                    <span>⚠️ {applyError}</span>
                  </div>
                )}

                <Input
                  label="First Name"
                  required
                  placeholder="e.g. Alex"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
                <Input
                  label="Last Name"
                  required
                  placeholder="e.g. Smith"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
                <Input
                  label="Email Address"
                  type="email"
                  required
                  placeholder="alex.smith@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <Input
                  label="Phone (Optional)"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />

                <div>
                  <label className="resume-field-label">
                    Resume / CV (PDF) <span style={{ color: '#ef4444' }}>*</span>
                  </label>

                  {selectedFile ? (
                    <div className="resume-selected-card">
                      <div className="resume-file-icon-wrap">
                        <FileText size={22} color="#10b981" />
                      </div>
                      <div className="resume-file-details">
                        <div className="resume-file-name" title={selectedFile.name}>
                          {selectedFile.name}
                        </div>
                        <div className="resume-file-size">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • Ready to upload
                        </div>
                      </div>
                      <button
                        type="button"
                        className="resume-remove-btn"
                        onClick={() => setSelectedFile(null)}
                        title="Remove file"
                        aria-label="Remove file"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="resume-dropzone-box">
                      <input
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setSelectedFile(e.target.files[0]);
                          }
                        }}
                      />
                      <div className="resume-dropzone-icon-circle">
                        <UploadCloud size={24} color="#6366f1" />
                      </div>
                      <div className="resume-dropzone-main-text">
                        <span className="resume-dropzone-browse">Click to upload</span> or drag & drop
                      </div>
                      <div className="resume-dropzone-sub-text">PDF format only (Max 10 MB)</div>
                    </label>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting}
                  style={{ width: '100%', marginTop: '8px' }}
                  icon={ArrowRight}
                >
                  Submit Application
                </Button>

                <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#64748b', marginTop: '8px' }}>
                  Already have an account?{' '}
                  <Link to="/login" style={{ color: '#818cf8' }}>
                    Sign in here
                  </Link>
                </p>
              </form>
            )}
          </aside>
        </div>
      </main>

      <footer className="public-footer">
        Powered by SmartHire AI Recruitment Cloud • Next-Gen Automated ATS Platform
      </footer>
    </div>
  );
}
